import { useEffect, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type DemandBgOption,
  type DemandRetailerOption,
  type DemandDetailKoma as ProtoDemandDetailKoma,
  type DemandPeakSummary as ProtoDemandPeakSummary,
} from '@ebalance/proto/bffuser/v1/balance_model_pb'
import { getDemandDetail } from '@ebalance/proto/bffuser/v1/balance_service-BalanceDemandService_connectquery'
import {
  type GetDemandDetailRequest,
  GetDemandDetailRequestSchema,
  type GetDemandDetailResponse,
} from '@ebalance/proto/bffuser/v1/balance_service_pb'
import {
  type UseQueryResult,
  keepPreviousData,
  useQueryClient,
} from '@tanstack/react-query'

import { isRegionLoading } from '~/hooks/isRegionLoading'
import { formatDateValue, toProtoDate } from '~/models/dateTime'
import type { GateCloseFrontier } from '~/models/gateClose'
import { gateCloseFrontierFromProto } from '~/models/gateClose'
import type { DetailFilterState } from '~/pages/BalanceDemandDetail/constants'
import {
  type BgChoices,
  includesUnattributedOfEffectiveBg,
  isDemandPlanSelectable,
  isIndividualBgCode,
  isProcurementNetSelectable,
  resolveEffectiveBgCode,
  resolveEffectiveRetailerCode,
  retailersOfEffectiveBg,
} from '~/pages/BalanceDemandDetail/models/bgFilterOptions'
import type {
  DetailKomaData,
  DetailSummary,
} from '~/pages/BalanceDemandDetail/models/generateMockData'
import { toActualStatus } from '~/pages/BalanceDemandDetail/models/generateMockData'

function convertKoma(k: ProtoDemandDetailKoma): DetailKomaData {
  return {
    koma: k.koma,
    time: k.time,
    // 旧 demand_plan へは fallback しない。BFF は旧 bundle 互換のため計画なしも
    // demand_plan に 0 を入れ続けるので、fallback すると計画なしと 0 を区別できない。
    demandPlan: k.demandPlanValue ?? null,
    demandActual: k.demandActual ?? null,
    actualStatus: toActualStatus(k.actualStatus),
    demandActualCorrected: k.demandActualCorrected ?? null,
    temperature: k.temperature ?? null,
    temperatureForecast: k.temperatureForecast ?? null,
    dewPoint: k.dewPoint ?? null,
    dewPointForecast: k.dewPointForecast ?? null,
    precipitation: k.precipitation ?? null,
    trendPlan: k.trendPlan ?? null,
    procurementNet: k.procurementNet ?? null,
    demandExtraHigh: k.demandExtraHigh ?? null,
    demandHigh: k.demandHigh ?? null,
    demandLow: k.demandLow ?? null,
    awaitingVoltageClasses: k.awaitingVoltageClasses,
  }
}

function convertSummary(s: ProtoDemandPeakSummary): DetailSummary {
  return {
    maxActual: s.maxActual,
    maxTime: s.maxTime,
    maxKoma: s.maxKoma,
    minActual: s.minActual,
    minTime: s.minTime,
    minKoma: s.minKoma,
  }
}

export function convertDetailResponse(resp: GetDemandDetailResponse): {
  komas: DetailKomaData[]
  summary: DetailSummary | null
  gcKoma: number
  gateCloseFrontier: GateCloseFrontier | undefined
  currentKoma: number
  bgOptions: DemandBgOption[]
} {
  return {
    komas: resp.komas.map(convertKoma),
    // summary 不在 (= 実績が無い、または実績のあるコマがすべて低圧未着) はそのまま null で通す。
    // 0 埋めすると未来日のサマリーバーが MAX 0 MWh を実測のように描く。
    summary: resp.summary != null ? convertSummary(resp.summary) : null,
    gcKoma: resp.gcKoma,
    gateCloseFrontier: gateCloseFrontierFromProto(resp.gateCloseFrontier),
    currentKoma: resp.currentKoma,
    bgOptions: resp.bgOptions,
  }
}

function demandDetailRequest(
  displayDate: Date,
  areaCode: AreaCode,
  filter: DetailFilterState,
): GetDemandDetailRequest {
  return create(GetDemandDetailRequestSchema, {
    displayDate: toProtoDate(displayDate),
    areaCode,
    // deprecated の retail には値を積まない。小売の選択は retailerCode で送る。
    bgCode: filter.bgCode,
    retailerCode: filter.retailerCode,
    demandGroup: filter.demandGroup,
    // 電圧階級は絞り込みから撤去した (issue #4734)。 proto の field は backend の
    // 階級選択パス撤去 (別 PR) まで残るが、 未設定 = 「すべて」 として扱われる。
    ratePlan: filter.ratePlan,
  })
}

function useDemandDetail(
  displayDate: Date,
  areaCode: AreaCode,
  filter: DetailFilterState,
): UseQueryResult<GetDemandDetailResponse, ConnectError> {
  return useQuery(
    getDemandDetail,
    demandDetailRequest(displayDate, areaCode, filter),
    {
      refetchInterval: 5_000,
      // 表示日・絞り込みを変えた直後も前の結果を残し、 影 + スピナーで覆う
      // (UI 辞書 components/region-loading.md)。 前の key の応答 (`isPlaceholderData`) は
      // 表示にだけ使い、 BG の選択の判断には使わない (下の useDemandDetailWithBgSelection)。
      placeholderData: keepPreviousData,
      // `ApiClientProvider` の既定は `throwOnError: true` (= 取得失敗を render 中に
      // throw して router の errorElement = 全画面エラーへ飛ばす)。 この画面は spec
      // AC-BDB-033 が**画面内のエラーバナー**を要求するので、 この query だけ false に
      // 上書きして `isError` を購読側で扱う (`WorkflowBackBar` と同じ形。 throwOnError
      // は observer ごとに評価される)。
      throwOnError: false,
    },
  )
}

/** FilterCard の BG Select が描く選択肢と、実際に効いている選択。 */
export type BgFilterSelection = {
  options: readonly DemandBgOption[]
  /** BG Select に「帰属不明」を並べるか (提出済み需要BGが 1 件ではない時)。 */
  unattributedSelectable: boolean
  /** 選択肢 (「帰属不明」を含む) に含まれる時だけ選択中の BG コード、それ以外は「すべて」('')。 */
  selectedCode: string
  /**
   * 小売 Select を操作できるか。根拠は「実際に効いている BG が個別 BG」であることだけ
   * (構成小売 0 名の BG でも有効で、選択肢は「BG 全体」のみ)。
   */
  isRetailerSelectEnabled: boolean
  /** 「調達量・販売量」の子トグルを操作でき、系列を出せるか。 */
  isProcurementNetEnabled: boolean
  /** 「需要計画値」のトグルを操作でき、系列を出せるか。 */
  isDemandPlanEnabled: boolean
  /** 実際に効いている BG の構成小売 (個別 BG でない時は空)。 */
  retailers: readonly DemandRetailerOption[]
  /** 小売 Select の末尾に「帰属不明」を並べるか (効いている BG の includesUnattributed)。 */
  isRetailerUnattributedSelectable: boolean
  /** 選択肢 (「帰属不明」を含む) に含まれる時だけ選択中の小売コード、それ以外は「BG 全体」('')。 */
  selectedRetailerCode: string
}

/**
 * GetDemandDetail の取得と BG フィルターの選択の扱いをまとめて持つ。選択を外す判断・
 * 外した後の state・その時の cache 投入は、いずれも #8687 の契約 (bg_options は
 * (表示日, エリア) だけで決まり bg_code に依存しない / 選択肢に無い bg_code には
 * 「すべて」の値で答える) に依存するので、1 か所で同じ値から組む。
 *
 * - bg_code を変えると request key が変わり応答が一時的に無くなるので、直前に取得
 *   できた選択肢を (表示日, エリア) 単位で保持し、応答待ち・取得失敗の間もその
 *   選択肢で判定する (保持しないと選んだ直後や取得失敗中に表示が「すべて」へ落ち、
 *   送っている絞り込みと食い違う)。表示日・エリアが変わったら保持分は変更前の日付の
 *   ものなので使わず、応答が届くまで「すべて」を返す
 * - 選択肢に含まれるかの判定は表示値と「外す」判断で同じ関数を使う。判定を 2 か所に
 *   持つと、bg_options に載らない選択肢 (帰属不明) の扱いを片方だけ直して、選んだ
 *   直後に「すべて」へ戻る事故になる。帰属不明を並べるかも bg_options と同じく
 *   応答ごとに変わる (表示日・エリアで提出済み需要BGの件数が変わる) ので一緒に保持する
 * - 外すのは取得に成功した応答で選択肢から消えた時だけ。応答待ち・取得失敗の間に
 *   外すと、その日に選択肢があるか分からないまま利用者の選択を捨てることになる
 * - 小売も同じ契機・同じ関数群で扱う。BG が外れる時は小売も同時に外れ、BG が残って
 *   小売だけ構成小売から消えた時は「BG 全体」へ戻す (契約上その応答は BG 全体の値)
 * - 外す時はその応答 (契約上すでに外した後の選択の値) を外した後の key に入れる。
 *   入れないと key が変わった瞬間に応答が無くなり、同じ内容を取り直すまで
 *   ローディング表示に落ちる
 */
export function useDemandDetailWithBgSelection(
  displayDate: Date,
  areaCode: AreaCode,
  filter: DetailFilterState,
  correctFilter: (filter: DetailFilterState) => void,
): {
  query: UseQueryResult<GetDemandDetailResponse, ConnectError>
  /** 領域を影 + スピナーで覆うか。 判断は `~/hooks/isRegionLoading`。 */
  isRegionLoading: boolean
  bgSelection: BgFilterSelection
} {
  const query = useDemandDetail(displayDate, areaCode, filter)
  // 前の key の応答 (表示日・絞り込みを変えた直後の placeholder) は、 その日・その選択の
  // 選択肢ではないので判断に使わない。 応答待ちと同じ扱い (保持した選択肢で判定し、 外さない)。
  const response = query.isPlaceholderData ? undefined : query.data
  const fetchedChoices = useMemo(
    () =>
      response == null
        ? undefined
        : {
            bgOptions: response.bgOptions,
            unattributedSelectable: response.unattributedSelectable,
          },
    [response],
  )
  const queryClient = useQueryClient()
  const transport = useTransport()

  const choicesKey = `${areaCode}:${formatDateValue(displayDate)}`
  const [retained, setRetained] = useState<{
    key: string
    choices: BgChoices
  } | null>(null)
  useEffect(() => {
    if (fetchedChoices == null) return
    setRetained({ key: choicesKey, choices: fetchedChoices })
  }, [fetchedChoices, choicesKey])

  useEffect(() => {
    if (response == null || fetchedChoices == null) return
    const effectiveBgCode = resolveEffectiveBgCode(
      filter.bgCode,
      fetchedChoices,
    )
    const effectiveRetailerCode = resolveEffectiveRetailerCode(
      filter.retailerCode,
      effectiveBgCode,
      response.bgOptions,
    )
    if (
      effectiveBgCode === filter.bgCode &&
      effectiveRetailerCode === filter.retailerCode
    ) {
      return
    }
    const effectiveFilter = {
      ...filter,
      bgCode: effectiveBgCode,
      retailerCode: effectiveRetailerCode,
    }
    queryClient.setQueryData(
      createConnectQueryKey({
        schema: getDemandDetail,
        input: demandDetailRequest(displayDate, areaCode, effectiveFilter),
        transport,
        cardinality: 'finite',
      }),
      response,
    )
    correctFilter(effectiveFilter)
  }, [
    response,
    fetchedChoices,
    filter,
    displayDate,
    areaCode,
    queryClient,
    transport,
    correctFilter,
  ])

  const choices = fetchedChoices ??
    (retained?.key === choicesKey ? retained.choices : undefined) ?? {
      bgOptions: [],
      unattributedSelectable: false,
    }
  const options = choices.bgOptions
  const selectedCode = resolveEffectiveBgCode(filter.bgCode, choices)
  const selectedRetailerCode = resolveEffectiveRetailerCode(
    filter.retailerCode,
    selectedCode,
    options,
  )
  return {
    query,
    isRegionLoading: isRegionLoading(query),
    bgSelection: {
      options,
      unattributedSelectable: choices.unattributedSelectable,
      selectedCode,
      isRetailerSelectEnabled: isIndividualBgCode(selectedCode),
      retailers: retailersOfEffectiveBg(selectedCode, options),
      isRetailerUnattributedSelectable: includesUnattributedOfEffectiveBg(
        selectedCode,
        options,
      ),
      selectedRetailerCode,
      isProcurementNetEnabled: isProcurementNetSelectable(
        selectedCode,
        selectedRetailerCode,
      ),
      isDemandPlanEnabled: isDemandPlanSelectable(
        selectedCode,
        selectedRetailerCode,
      ),
    },
  }
}
